import NumberInput from "../../../components/NumberInput";
import { useEffect, useState } from "react";
import { FieldFeedback, RequiredMark } from "../../../components/FormValidation";
import { getBaseUnitValue, getUnitFactorToBase } from "../../../utils/helpers/pricing";

const unitChoices = [
  { value: "gram", label: "Berat", example: "Contoh: roasted beans dipakai 18 gram untuk satu resep." },
  { value: "ml", label: "Volume", example: "Contoh: susu UHT dipakai 150 ml untuk satu resep." },
  { value: "pcs", label: "Jumlah", example: "Contoh: cup dipakai 1 pcs untuk satu minuman." },
];

const MaterialUnitFields = ({ formState, setFormState, options, stockUnitLabel, validation, isLoading }) => {
  const baseUnit = formState?.unit ? getBaseUnitValue(options, formState.unit) : "";
  const group = baseUnit ? (["gram", "ml"].includes(baseUnit) ? baseUnit : "pcs") : "";
  const selected = unitChoices.find((choice) => choice.value === group);
  const [packagingOpen, setPackagingOpen] = useState(false);
  const usesContents = ["gram", "ml"].includes(baseUnit);
  const factor = getUnitFactorToBase(options, formState?.unit);
  const inputUnitLabel = factor === 1 ? stockUnitLabel : formState?.unit;

  useEffect(() => {
    if (validation.errors.weight) setPackagingOpen(true);
  }, [validation.errors.weight]);

  const chooseUnit = (value) => {
    // Opening an old material (or clicking its current group) must not normalize its saved unit.
    if (group === value) return;
    const option = options.find((unit) => unit.value === value)
      || options.find((unit) => getBaseUnitValue(options, unit.value) === value
        && getUnitFactorToBase(options, unit.value) === 1);
    if (!option) return;
    setFormState((previous) => ({ ...previous, unit: option.value, weight: "" }));
  };

  return (
    <div className="mb-3">
      <fieldset className="m-0 p-0 border-0">
        <legend className="form-label fs-5 mb-2">Satuan Stok &amp; Resep<RequiredMark /></legend>
        <div className="form-selectgroup d-flex flex-nowrap gap-2 m-0">
          {unitChoices.map((choice) => {
            const available = options.some((unit) => getBaseUnitValue(options, unit.value) === choice.value
              && getUnitFactorToBase(options, unit.value) === 1);
            return (
              <label key={choice.value} className="form-selectgroup-item flex-fill m-0">
                <input
                  {...validation.fieldProps("unit")}
                  type="radio"
                  className="form-selectgroup-input"
                  value={choice.value}
                  checked={group === choice.value}
                  disabled={isLoading || (!available && group !== choice.value)}
                  onChange={() => chooseUnit(choice.value)}
                />
                <span className="form-selectgroup-label py-2 px-2">
                  <span className="fw-semibold">{choice.label}</span>
                  <span className="d-block small">{group === choice.value ? stockUnitLabel : choice.value}</span>
                </span>
              </label>
            );
          })}
        </div>
        <FieldFeedback formId="material" name="unit" error={validation.errors.unit} />
        <div className="form-hint mt-2" aria-live="polite">
          {selected
            ? group === "pcs" && baseUnit !== "pcs"
              ? `Stok dan resep dihitung dalam ${stockUnitLabel}.`
              : selected.example
            : "Pilih satuan yang dipakai saat menakar bahan dalam resep."}
        </div>
      </fieldset>

      {selected ? (
        <details className="mt-3" open={packagingOpen} onToggle={(event) => setPackagingOpen(event.currentTarget.open)}>
          <summary className="text-secondary small cursor-pointer">Kemasan pembelian (opsional)</summary>
          {usesContents ? (
            <div className="mt-2">
              <label className="form-label" htmlFor="material-package-contents">Isi per kemasan ({inputUnitLabel})</label>
              <div className="input-group">
                <NumberInput
                  {...validation.fieldProps("weight")}
                  id="material-package-contents"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min="0"
                  className={validation.inputClass("weight")}
                  value={formState?.weight ?? ""}
                  placeholder={factor === 1 ? "Contoh: 1000" : "Contoh: 1"}
                  disabled={isLoading}
                  onWheel={(event) => event.target.blur()}
                  onChange={(event) => setFormState((previous) => ({ ...previous, weight: event.target.value }))}
                />
                <span className="input-group-text">{inputUnitLabel}</span>
              </div>
              <FieldFeedback formId="material" name="weight" error={validation.errors.weight} />
              <div className="form-hint mt-1">Isian awal saat pembelian; bisa diubah sesuai kemasan yang dibeli.</div>
            </div>
          ) : (
            <p className="form-hint mt-2 mb-0">Masukkan jumlah dalam {stockUnitLabel} saat pembelian. Jika membeli per pak atau dus, gunakan satuan dengan konversi yang sudah diatur di Master Satuan.</p>
          )}
        </details>
      ) : null}
    </div>
  );
};

export default MaterialUnitFields;
